import $ from 'jquery';
import CustomEvent from 'custom-event';
import FilterGroup from './filter-group';

const namespace = 'filters';

export default class Filters {
  state = {
    lastValues: [],
  };

  constructor(el, options) {
    this.options = $.extend(
      true,
      {
        selectors: {
          group: '.filters__group',
          apply: '.js-filters__apply',
          clear: '.js-filters__clear',
        },
        classes: {
          visible: 'is-visible',
        },
        data: {
          page: 'data-filter-page',
        },
        updateOnClick: false,
        isControlled: false,
      },
      options,
    );

    this.$el = $(el);
    this.$apply = $(this.options.selectors.apply, this.$el);
    this.$groups = $(this.options.selectors.group, this.$el);
    this.$clear = $(this.options.selectors.clear, this.$el);

    const pagePath = this.$el.attr(this.options.data.page);
    this.baseUrl = window.location.origin + pagePath;

    this.bind();
    this.$filterGroups = this.initGroups();
    this.state.lastValues = this.getVals();

    if (!this.options.updateOnClick) {
      this.updateActions();
    }

    this.$apply.attr('disabled', true);
  }

  bind() {
    this.$el.on(
      `click.${namespace}`,
      this.options.selectors.apply,
      this.handleSubmit.bind(this),
    );

    this.$el.on(
      `click.clear.${namespace}`,
      this.options.selectors.clear,
      this.handleClear.bind(this),
    );

    this.$el.on('filtergroup:changed', this.handleChange.bind(this));

    $(document).on('collection:updated', this.handleUpdate.bind(this));
  }

  /**
   * @param {Event} event
   */
  handleClear(event) {
    event.preventDefault();

    this.resetVals();
    this.handleSubmit();
  }

  handleChange() {
    const event = new CustomEvent(`${namespace}:changed`);
    document.dispatchEvent(event);

    if (this.options.updateOnClick) {
      return this.handleSubmit();
    }

    return this.updateActions();
  }

  handleSubmit() {
    const location = this.getLocation();

    if (this.options.isControlled) {
      this.updateTrigger();

      this.$el.trigger(`${namespace}:applied`, {
        tagGroups: this.getGroupedVals(),
      });

      return;
    }

    window.location = location;
  }

  handleUpdate() {
    this.state.lastValues = this.getVals();

    if (!this.options.updateOnClick) {
      this.updateActions();
    }
  }

  getLocation() {
    const tags = this.getVals().join('+');
    const params = new window.URLSearchParams(window.location.search);

    params.delete('page');

    return `${this.baseUrl}/#${tags}${params.toString() ? `?${params}` : ''}`;
  }

  updateActions() {
    const selected = this.getVals()
      .sort()
      .join('+');
    const initial = this.state.lastValues
      .slice()
      .sort()
      .join('+');

    if (selected !== initial) {
      this.$apply.attr('disabled', false);
      return;
    }

    this.$apply.attr('disabled', true);
  }

  /**
   * @returns {Event}
   */
  updateTrigger() {
    const count = this.getCount();
    const isVisible = (count >= 1);

    this.$clear.toggleClass(this.options.classes.visible, isVisible);
    this.$el.trigger(`${namespace}:count`, {
      count,
    });
  }

  /**
   * @returns {Array}
   */
  initGroups() {
    return this.$groups.map((_, item) => {
      return new FilterGroup(item);
    });
  }

  /**
   * @returns {Object[]}
   */
  getGroupedVals() {
    return this.getGroupedValsProp('vals');
  }

  /**
   * @returns {Object[]}
   */
  getGroupedOptions() {
    return this.getGroupedValsProp('valueOptions');
  }

  /**
   * @returns {Object[]}
   */
  getGroupedValsProp(prop) {
    return this.$filterGroups.map((_, group) => {
      return {
        name: group.name || '',
        vals: group[prop] || '',
      };
    }).toArray();
  }

  /**
   * @param {Object[]} groups
   */
  disableGroupedVals(groups) {
    const groupArray = this.$filterGroups.toArray();

    groups.forEach(group => {
      const instance = groupArray.find(inst => inst.name === group.name);
      instance.disableOptions(group.vals);
    });
  }

  /**
   * Enables all group values
   */
  enableGroupedVals() {
    const groupArray = this.$filterGroups.toArray();

    groupArray.forEach(group => {
      group.disableOptions([]);
    });
  }

  /**
   * @returns {Array}
   */
  getVals() {
    return this.$filterGroups.map((_, group) => group.vals).toArray();
  }

  /**
   * @param {Object[]} groups
   */
  setGroupVals(groups) {
    const groupArray = this.$filterGroups.toArray();

    groups.forEach(group => {
      const instance = groupArray.find(inst => inst.name === group.name);
      instance.setSelected(group.vals);
    });

    this.updateActions();
    this.updateTrigger();
  }

  resetVals() {
    this.$filterGroups.each((_, group) => {
      group.clear();
    });
  }

  /**
   * @returns {Number}
   */
  getCount() {
    return this.getVals().length;
  }
}
